import { Position } from '@xyflow/react';
import {
  buildOrthogonalEdge,
  buildParallelPath,
  buildSelfLoopPath,
  buildSideRoutedEdge,
  getParallelEndpoints,
  type NodeRect,
  type Point,
} from './flowchart-edge-geometry';
import type { LayoutCenter } from './flowchart-edge-node-model';

const EDGE_BORDER_RADIUS = 14;
const BACK_EDGE_MARGIN_OFFSET = 34;
const DEFAULT_SELF_LOOP_RADIUS = 36;

interface EdgeGeometry {
  labelPosition: Point;
  path: string;
}

export function resolveFlowchartEdgeGeometry(input: {
  isBack: boolean;
  isSelfLoop: boolean;
  layoutCenter: LayoutCenter;
  parallelOffset: number;
  selfLoopRadius: number;
  sourcePosition: Position;
  sourceRect: NodeRect;
  targetPosition: Position;
  targetRect: NodeRect;
}): EdgeGeometry {
  if (input.isSelfLoop) {
    const loop = buildSelfLoopPath({
      center: input.sourceRect.center,
      size: input.sourceRect.size,
      radius: input.selfLoopRadius > 0
        ? input.selfLoopRadius
        : DEFAULT_SELF_LOOP_RADIUS,
    });
    return { path: loop.path, labelPosition: loop.labelPosition };
  }

  if (input.parallelOffset !== 0) {
    const endpoints = getParallelEndpoints({
      source: input.sourceRect,
      target: input.targetRect,
      offset: input.parallelOffset,
    });
    const bowed = buildParallelPath({
      source: endpoints.source,
      target: endpoints.target,
      offset: input.parallelOffset,
    });
    return { path: bowed.path, labelPosition: bowed.labelPosition };
  }

  if (input.isBack) {
    const sideRouted = buildSideRoutedEdge({
      source: input.sourceRect,
      target: input.targetRect,
      sourcePosition: input.sourcePosition,
      layoutCenterX: input.layoutCenter.x,
      layoutCenterY: input.layoutCenter.y,
      borderRadius: EDGE_BORDER_RADIUS,
      marginOffset: BACK_EDGE_MARGIN_OFFSET,
    });
    return { path: sideRouted.path, labelPosition: sideRouted.labelPosition };
  }

  const orthogonal = buildOrthogonalEdge({
    source: input.sourceRect,
    target: input.targetRect,
    sourcePosition: input.sourcePosition,
    targetPosition: input.targetPosition,
    borderRadius: EDGE_BORDER_RADIUS,
  });
  return { path: orthogonal.path, labelPosition: orthogonal.labelPosition };
}
